import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../utils/api';
import { Search, ExternalLink, AlertCircle, Zap, Check, Plus, X, List } from 'lucide-react';

export default function TicketPicker({ team, onSelect, onQueue, onClose, queueMode = false }) {
  const [query, setQuery] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState([]); // keys of queued tickets
  const [mode, setMode] = useState(queueMode ? 'queue' : 'single'); // 'single' | 'queue'
  const debounceRef = useRef(null);

  const search = async (q) => {
    setLoading(true);
    setError('');
    try {
      const results = await api.jira.tickets(team.jira_project_key, q);
      setTickets(results);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { search(''); }, []);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 400);
  };

  const toggleSelect = (ticket) => {
    setSelected(prev =>
      prev.find(t => t.key === ticket.key)
        ? prev.filter(t => t.key !== ticket.key)
        : [...prev, ticket]
    );
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    setSelected(prev => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const moveDown = (idx) => {
    setSelected(prev => {
      if (idx === prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  const priorityColor = (p) => ({
    'Highest': 'var(--red)', 'High': 'var(--red)',
    'Medium': 'var(--yellow)', 'Low': 'var(--blue)', 'Lowest': 'var(--blue)'
  }[p] || 'var(--text-muted)');

  const isSelected = (key) => selected.some(t => t.key === key);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-wide" style={{ maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div>
            <h3>Pick Tickets</h3>
            <p className="text-sm text-muted">{team.jira_project_key} · Open tickets only</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Mode toggle */}
            <div style={{
              display: 'flex', background: 'var(--bg-elevated)',
              border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden'
            }}>
              <button
                className="btn btn-sm"
                style={{
                  borderRadius: 0, border: 'none',
                  background: mode === 'single' ? 'var(--accent)' : 'transparent',
                  color: mode === 'single' ? 'white' : 'var(--text-secondary)'
                }}
                onClick={() => setMode('single')}
                title="Pick one ticket"
              >
                <Plus size={12} /> Single
              </button>
              <button
                className="btn btn-sm"
                style={{
                  borderRadius: 0, border: 'none',
                  background: mode === 'queue' ? 'var(--accent)' : 'transparent',
                  color: mode === 'queue' ? 'white' : 'var(--text-secondary)'
                }}
                onClick={() => setMode('queue')}
                title="Build a queue of tickets"
              >
                <List size={12} /> Queue
              </button>
            </div>
            <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
          </div>
        </div>

        {mode === 'queue' && selected.length > 0 && (
          <div style={{
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', padding: '12px 16px', marginBottom: 12
          }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold">Queue ({selected.length} tickets)</span>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => { onQueue(selected); onClose(); }}
              >
                <List size={12} /> Start Queue
              </button>
            </div>
            <div className="flex-col gap-1">
              {selected.map((t, idx) => (
                <div key={t.key} className="flex items-center gap-2" style={{ fontSize: 12 }}>
                  <span style={{ color: 'var(--text-muted)', width: 16, textAlign: 'right', flexShrink: 0 }}>{idx + 1}.</span>
                  <span className="badge badge-muted font-mono" style={{ padding: '1px 6px' }}>{t.key}</span>
                  <span className="truncate flex-1">{t.summary}</span>
                  <button className="btn btn-ghost btn-icon btn-sm" onClick={() => moveUp(idx)} disabled={idx === 0} style={{ padding: '1px 4px' }}>↑</button>
                  <button className="btn btn-ghost btn-icon btn-sm" onClick={() => moveDown(idx)} disabled={idx === selected.length - 1} style={{ padding: '1px 4px' }}>↓</button>
                  <button className="btn btn-ghost btn-icon btn-sm" onClick={() => toggleSelect(t)} style={{ padding: '1px 4px', color: 'var(--red)' }}><X size={10} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="input-group" style={{ marginBottom: 12 }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              className="input"
              style={{ paddingLeft: 36 }}
              value={query}
              onChange={handleQueryChange}
              placeholder="Search by ticket ID or summary..."
              autoFocus
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && (
            <div className="flex items-center justify-center" style={{ padding: 32 }}>
              <div className="pulse text-muted">Searching Jira...</div>
            </div>
          )}

          {error && <div className="toast toast-error mb-4"><AlertCircle size={14} /> {error}</div>}

          {!loading && tickets.length === 0 && !error && (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <p className="text-muted">No open tickets found{query ? ` for "${query}"` : ''}.</p>
            </div>
          )}

          <div className="flex-col gap-2">
            {tickets.map(ticket => {
              const sel = isSelected(ticket.key);
              return (
                <div
                  key={ticket.key}
                  className={`ticket-card ${sel ? 'selected' : ''}`}
                  onClick={() => {
                    if (mode === 'single') { onSelect(ticket); onClose(); }
                    else toggleSelect(ticket);
                  }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="badge badge-muted font-mono">{ticket.key}</span>
                    {ticket.type && <span className="text-xs text-muted">{ticket.type}</span>}
                    {ticket.priority && (
                      <span className="text-xs font-bold" style={{ color: priorityColor(ticket.priority), marginLeft: 'auto' }}>
                        ● {ticket.priority}
                      </span>
                    )}
                    {ticket.currentPoints != null && (
                      <span className="badge badge-purple">
                        <Zap size={10} /> {ticket.currentPoints}pts
                      </span>
                    )}
                    {mode === 'queue' && sel && (
                      <span className="badge badge-green" style={{ marginLeft: sel ? 0 : 'auto' }}>
                        <Check size={10} /> #{selected.findIndex(t => t.key === ticket.key) + 1}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-bold" style={{ marginBottom: 4 }}>{ticket.summary}</p>
                  <div className="flex items-center gap-2">
                    <span className="badge badge-muted">{ticket.status}</span>
                    <a
                      href={ticket.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-muted flex items-center gap-1"
                      onClick={e => e.stopPropagation()}
                      style={{ marginLeft: 'auto' }}
                    >
                      Open in Jira <ExternalLink size={10} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {mode === 'queue' && (
          <div style={{ paddingTop: 12, borderTop: '1px solid var(--border)', marginTop: 8 }}>
            <button
              className="btn btn-primary w-full"
              disabled={selected.length === 0}
              onClick={() => { onQueue(selected); onClose(); }}
            >
              <List size={14} /> Start Queue ({selected.length} ticket{selected.length !== 1 ? 's' : ''})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
